import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutations and queries
export const CREATE_TICKET_BONUS_MUTATION = gql`
  mutation CreateTicketBonusType($input: CreateTicketBonusInput!) {
    createTicketBonusType(input: $input) {
      id
      name
      description
      code
      type
      period
      startDate
      endDate
      isActive
      conditions
      environmentId
    }
  }
`;

export const GET_TICKET_TYPES_QUERY = gql`
  query GetTicketTypes($environmentId: Int) {
    ticketTypes(environmentId: $environmentId) {
      period
      tickets {
        id
        name
        code
        expiresAt
        meta
        environmentId
      }
    }
  }
`;

export const UPDATE_TICKET_BONUS_MUTATION = gql`
  mutation UpdateTicketBonusType($input: UpdateTicketBonusInput!) {
    updateTicketBonusType(input: $input) {
      id
      name
      description
      code
      type
      period
      startDate
      endDate
      isActive
      conditions
    }
  }
`;

export const DELETE_TICKET_BONUS_MUTATION = gql`
  mutation DeleteTicketBonus($id: Int!) {
    deleteTicketBonus(id: $id)
  }
`;

export const GET_TICKET_PARTICIPANTS_QUERY = gql`
  query GetTicketParticipants($bonusTypeId: Int!) {
    ticketParticipants(bonusTypeId: $bonusTypeId) {
      bonusTypeId
      bonusName
      bonusCode
      period
      environmentId
      startDate
      endDate
      isExpired
      isActive
      isDrawn
      totalUsers
      totalTickets
      ticketPrice
      prizeMeta
      participants {
        userId
        username
        totalTickets
      }
      winner {
        userId
        username
        totalTickets
        drawnAt
      }
    }
  }
`;

export const DRAW_TICKET_WINNER_MUTATION = gql`
  mutation DrawTicketWinner($bonusTypeId: Int!) {
    drawTicketWinner(bonusTypeId: $bonusTypeId) {
      bonusTypeId
      bonusName
      bonusCode
      period
      winnerUserId
      winnerTickets
      totalTickets
      drawnAt
      prizeMeta
    }
  }
`;

// Types
export enum PeriodType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export interface TicketBonusConditions {
  prizeType: string;
  winAmount?: number | null;
  ticketPrice: number;
  cashinFreeTicketsEnabled?: boolean;
  cashinFreeTicketsPercent?: number;
}

export interface TicketBonus {
  id: number;
  name: string;
  description: string;
  code: string;
  type?: string;
  period: PeriodType;
  startDate: string;
  endDate: string;
  isActive: boolean;
  conditions: TicketBonusConditions;
}

export interface Ticket {
  id: string;
  name: string;
  code: string;
  expiresAt: string;
  meta: any;
}

export interface TicketType {
  period: string;
  tickets: Ticket[];
}

export interface CreateTicketBonusInput {
  name: string;
  description: string;
  code: string;
  period: PeriodType;
  startDate: string;
  endDate: string;
  isActive: boolean;
  autoIssue: boolean;
  isStackable: boolean;
  conditions: TicketBonusConditions;
  environmentId?: number;
}

export interface UpdateTicketBonusInput {
  id: number;
  name?: string;
  description?: string;
  period?: PeriodType;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  conditions?: TicketBonusConditions;
}

export interface TicketParticipant {
  userId: number;
  username: string;
  totalTickets: number;
}

export interface TicketWinner {
  userId: number;
  username: string;
  totalTickets: number;
  drawnAt: string;
}

export interface TicketParticipants {
  bonusTypeId: number;
  bonusName: string;
  bonusCode: string;
  period: string;
  environmentId: number | null;
  startDate: string;
  endDate: string;
  isExpired: boolean;
  isActive: boolean;
  isDrawn: boolean;
  totalUsers: number;
  totalTickets: number;
  ticketPrice: number;
  prizeMeta: any;
  participants: TicketParticipant[];
  winner: TicketWinner | null;
}

export interface DrawTicketWinnerResult {
  bonusTypeId: number;
  bonusName: string;
  bonusCode: string;
  period: string;
  winnerUserId: number | null;
  winnerTickets: number | null;
  totalTickets: number;
  drawnAt: string;
  prizeMeta: any;
}

// Helper function to generate ticket code with date suffix
export const generateTicketCode = (prizeType: string, period: PeriodType): string => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const dateSuffix = `${day}${month}${year}`;
  
  return `${prizeType}_TICKET_${period}_${dateSuffix}`;
};

// Service class
class RaffleService {
  async createTicketBonus(input: CreateTicketBonusInput): Promise<TicketBonus> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: CREATE_TICKET_BONUS_MUTATION,
        variables: { input },
      });
      return data.createTicketBonusType;
    } catch (error) {
      console.error('Error creating ticket bonus:', error);
      throw error;
    }
  }

  async getTicketBonuses(environmentId?: number): Promise<TicketBonus[]> {
    try {
      const { data } = await apolloClient.query({
        query: GET_TICKET_TYPES_QUERY,
        variables: { environmentId: environmentId || undefined },
        fetchPolicy: 'network-only',
      });
      return data.ticketTypes;
    } catch (error) {
      console.error('Error fetching ticket types:', error);
      throw error;
    }
  }

  async getTicketTypes(environmentId?: number): Promise<TicketType[]> {
    try {
      const { data } = await apolloClient.query({
        query: GET_TICKET_TYPES_QUERY,
        variables: { environmentId: environmentId || undefined },
        fetchPolicy: 'network-only',
      });
      return data.ticketTypes;
    } catch (error) {
      console.error('Error fetching ticket types:', error);
      throw error;
    }
  }

  async updateTicketBonus(input: UpdateTicketBonusInput): Promise<TicketBonus> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPDATE_TICKET_BONUS_MUTATION,
        variables: { input },
      });
      return data.updateTicketBonusType;
    } catch (error) {
      console.error('Error updating ticket bonus:', error);
      throw error;
    }
  }

  async deleteTicketBonus(id: number): Promise<boolean> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: DELETE_TICKET_BONUS_MUTATION,
        variables: { id },
      });
      return data.deleteTicketBonus;
    } catch (error) {
      console.error('Error deleting ticket bonus:', error);
      throw error;
    }
  }

  async drawTicketWinner(bonusTypeId: number): Promise<DrawTicketWinnerResult> {
    try {
      const { data, errors } = await apolloClient.mutate({
        mutation: DRAW_TICKET_WINNER_MUTATION,
        variables: { bonusTypeId },
      });
      // errorPolicy is 'all', so GraphQL errors are returned here instead of thrown
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }
      if (!data?.drawTicketWinner) {
        throw new Error('Failed to draw winner.');
      }
      return data.drawTicketWinner;
    } catch (error) {
      console.error('Error drawing ticket winner:', error);
      throw error;
    }
  }

  async getTicketParticipants(bonusTypeId: number): Promise<TicketParticipants> {
    try {
      const { data } = await apolloClient.query({
        query: GET_TICKET_PARTICIPANTS_QUERY,
        variables: { bonusTypeId },
        fetchPolicy: 'network-only',
      });
      return data.ticketParticipants;
    } catch (error) {
      console.error('Error fetching ticket participants:', error);
      throw error;
    }
  }
}

export const raffleService = new RaffleService();
